import React, { useState, useEffect, useRef, useCallback } from 'react';
import { clearTypewriterPosition } from './useSmoothTypewriter';
import './chatbot.css';
import 'katex/dist/katex.min.css';
import { useChatStore } from '../../store/chatStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { userAppContext } from '../../context/Context';
import {
  ArrowUpIcon,
  CheckIcon,
  TrashIcon,
  ArrowDownTrayIcon as ArrowDownTrayOutline,
  PlusIcon,
  InformationCircleIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  BellIcon,
  BellSlashIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import StopIcon from '@heroicons/react/24/solid/StopIcon';
import RestrictedAccess from '../Modal/RestrictedAccess';
import type { ChatMessageType } from '@/store/types';
import ReasoningBlock from './ReasoningBlock';
import BotMessageText from './BotMessageText';
import QuestionRail from './QuestionRail';
import { trackEvent } from '@/utils/analytics';
import { Link } from 'react-router-dom';
import 'highlight.js/styles/github-dark.css';
import { clearBackendConversation, createChatSessionId } from '@/utils/chatConversation';
import { peekAidaLaunchParams, readAidaLaunchParams } from '@/utils/openAidaWithContext';
import { normalizeBotMarkdown } from './normalizeBotMarkdown';
import type { ApiEndpointSearchLink } from './parseApiEndpoint';
import CodeBlock from './CodeBlock';
import BotMessageActions from './BotMessageActions';
import { sanitizeDownloadFilename, triggerBlobDownload } from './aidaClipboard';

interface ChatBotProps {
  chatId?: string;
  message?: string;
  question?: string;
  shortQuestion?: string;
  autoSend?: boolean;
  style?: React.CSSProperties;
}

export default function ChatBot({
  chatId,
  message = '',
  question: questionProp = '',
  shortQuestion: shortQuestionProp = '',
  autoSend: autoSendProp = false,
  style = {},
}: ChatBotProps) {
  const [launchParams] = useState(() => readAidaLaunchParams());
  const question = launchParams?.question ?? questionProp;
  const autoSend = launchParams?.autoSend ?? autoSendProp;
  const { isAuthenticated, userApiKey, groups, user, authorization, restrictSources } = userAppContext();
  const searchSource = restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
  const normalizedGroups = Array.isArray(groups) ? groups : [];
  const trackingGroup =
    normalizedGroups
      .find((group: string) => group.startsWith('ROLE_'))
      ?.replace('ROLE_', '')
      .toLowerCase() || (isAuthenticated ? 'authenticated' : 'anonymous');

  const {
    chatHistory,
    chatSessions,
    aidaNotificationsEnabled,
    composerAlert,
    setChatHistory,
    setAidaNotificationsEnabled,
    clearComposerAlert,
    ensureSession,
    addUserMessage,
    streamBotResponse,
    stopStreaming,
    finalizeStreamMessage,
    syncBotInterruptedDisplay,
    clearSession,
    deleteSession,
    renameSession,
    removeMessagesFrom,
  } = useChatStore();

  const [downloadId, setDownloadId] = useState<string | null>(null);
  const [inputMessage, setInputMessage] = useState(() => {
    const sq = launchParams?.shortQuestion ?? shortQuestionProp;
    if (sq) return sq;
    const q = launchParams?.question ?? questionProp;
    const willAutoSend = launchParams?.autoSend ?? autoSendProp;
    if (q && !willAutoSend) return q;
    return '';
  });
  const [hasAutoSent, setHasAutoSent] = useState(false);
  const autoScrollRef = useRef(true);
  const userScrolledUpRef = useRef(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const userName = user?.['custom:name'];
  const [activeChatId, setActiveChatId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlChatId = params.get('chatId');
    if (urlChatId) return urlChatId;
    if (chatId) return chatId;

    const hasPendingLaunch = Boolean(peekAidaLaunchParams());
    if (!hasPendingLaunch) {
      const storedSessions = useChatStore.getState().chatSessions;
      const existingEmpty = Object.entries(storedSessions).find(
        ([, s]) => !s.messages.some(m => m.sender === 'user'),
      );
      if (existingEmpty) return existingEmpty[0];
    }
    return createChatSessionId();
  });
  const session = chatSessions[activeChatId];
  const messages = session?.messages ?? [];
  const isBotResponding = session?.isStreaming ?? false;
  const isBotTyping = session?.isStreaming ?? false;
  const botStatus = session?.botStatus ?? null;
  const streamBufferClosed = session?.streamBufferClosed ?? false;
  const revealFinalizeRef = useRef(false);
  const initialActiveChatIdRef = useRef(activeChatId);

  const [isAnswerTypingVisible, setIsAnswerTypingVisible] = useState(false);
  const typingStartedRef = useRef(false);

  useEffect(() => {
    if (isBotResponding) {
      typingStartedRef.current = false;
      setIsAnswerTypingVisible(false);
    }
  }, [isBotResponding]);

  const showComposerAlert = composerAlert && composerAlert.chatId === activeChatId ? composerAlert : null;

  const hasConversation = messages.some(m => m.sender === 'user');
  const inputPlaceholder = hasConversation
    ? 'Ask AIDA anything…'
    : 'Do you want to see what kind of questions I can help you answer?';
  const userQuestions = messages.filter(m => m.sender === 'user');
  const showQuestionRail = userQuestions.length >= 5;

  const hasNewConversationTab = Object.values(chatSessions).some(
    s => !s.messages.some(m => m.sender === 'user'),
  );

  const initialBotMessage: ChatMessageType = {
    id: 'bot',
    sender: 'bot',
    message:
      message !== ''
        ? message
        : 'Hello! How can I help you?\nDo you want to see what kind of questions I can help you answer?',
    timestamp: new Date(),
  };

  const activeChatIdRef = useRef(activeChatId);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('chatId')) {
      const clean = window.location.pathname;
      window.history.replaceState({}, '', clean);
    }
  }, []);

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  useEffect(() => {
    ensureSession(activeChatId, initialBotMessage);
    if (chatHistory.length === 0) {
      setChatHistory([initialBotMessage]);
    }
  }, [activeChatId, ensureSession, setChatHistory]);

  const scrollMessagesToBottom = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const followStreamScroll = useCallback(() => {
    if (userScrolledUpRef.current) return;
    scrollMessagesToBottom();
  }, [scrollMessagesToBottom]);

  const scrollToBottomForNewTurn = useCallback(() => {
    userScrolledUpRef.current = false;
    autoScrollRef.current = true;
    scrollMessagesToBottom();
  }, [scrollMessagesToBottom]);

  useEffect(() => {
    userScrolledUpRef.current = false;
    autoScrollRef.current = true;
    const rafId = window.requestAnimationFrame(() => {
      scrollMessagesToBottom();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [activeChatId, scrollMessagesToBottom]);

  const scrollToQuestion = (messageId: string) => {
    const container = messagesAreaRef.current;
    const messageEl = document.getElementById(`aida-user-msg-${messageId}`);
    if (!container || !messageEl) return;

    autoScrollRef.current = false;

    const containerRect = container.getBoundingClientRect();
    const messageRect = messageEl.getBoundingClientRect();
    const messageTopInContainer = messageRect.top - containerRect.top + container.scrollTop;
    const targetScrollTop = messageTopInContainer - container.clientHeight / 2 + messageRect.height / 2;
    const maxScrollTop = container.scrollHeight - container.clientHeight;

    container.scrollTo({
      top: Math.max(0, Math.min(targetScrollTop, maxScrollTop)),
      behavior: 'smooth',
    });

    messageEl.classList.add('aida-msg--highlight');
    window.setTimeout(() => messageEl.classList.remove('aida-msg--highlight'), 1600);
  };

  // Separar los dos handlers que ahora comparten la misma función:

  const handleNewChat = () => {
    if (hasNewConversationTab) return;
    trackEvent({
      name: 'aida_new_chat',
      buttonId: `aida_new_chat_${trackingGroup}`,
      label: 'New chat',
      page: 'aida',
      section: 'sidebar',
      feature: 'aida',
      eventCategory: 'aida',
    });
    const newId = createChatSessionId();
    ensureSession(newId, initialBotMessage);
    setActiveChatId(newId);
    if (isMobile) setSidebarCollapsed(true);
  };

  const handleEndChat = async () => {
    trackEvent({
      name: 'aida_clear_chat',
      buttonId: `aida_clear_chat_${trackingGroup}`,
      label: 'Clear chat',
      page: 'aida',
      section: 'sidebar',
      feature: 'aida',
      eventCategory: 'aida',
    });
    const currentChatId = activeChatIdRef.current;
    try {
      await clearBackendConversation({
        userSub: user?.sub,
        authorization,
        chatSessionId: currentChatId,
      });
      stopStreaming(currentChatId, false);
      // Remove any other empty sessions to keep only one "new conversation"
      Object.entries(chatSessions).forEach(([id, s]) => {
        if (id !== currentChatId && !s.messages.some(m => m.sender === 'user')) {
          deleteSession(id);
        }
      });
      clearSession(currentChatId, initialBotMessage);
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }
  };

  const handleLoadSession = (id: string) => {
    setActiveChatId(id);
    if (isMobile) setSidebarCollapsed(true);
  };

  const handleDeleteSession = (id: string) => {
    clearBackendConversation({
      userSub: user?.sub,
      authorization,
      chatSessionId: id,
    }).catch(err => console.error('Failed to clear backend conversation on delete:', err));

    if (activeChatId === id) {
      const remaining = Object.entries(chatSessions).filter(([otherId]) => otherId !== id);
      deleteSession(id);
      if (remaining.length > 0) {
        const sorted = remaining.sort((a, b) => {
          const aMsg = a[1].messages.find(m => m.sender === 'user');
          const bMsg = b[1].messages.find(m => m.sender === 'user');
          return new Date(bMsg?.timestamp ?? 0).getTime() - new Date(aMsg?.timestamp ?? 0).getTime();
        });
        setActiveChatId(sorted[0][0]);
      } else {
        const newId = createChatSessionId();
        ensureSession(newId, initialBotMessage);
        setActiveChatId(newId);
      }
      if (isMobile) setSidebarCollapsed(true);
    } else {
      deleteSession(id);
    }
  };

  useEffect(() => {
    const el = messagesAreaRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        userScrolledUpRef.current = true;
        autoScrollRef.current = false;
      } else {
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom < 80) {
          userScrolledUpRef.current = false;
          autoScrollRef.current = true;
        }
      }
    };

    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const deltaY = touchStartY - (e.touches[0]?.clientY ?? 0);
      if (deltaY < 0) {
        userScrolledUpRef.current = true;
        autoScrollRef.current = false;
      } else {
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom < 80) {
          userScrolledUpRef.current = false;
          autoScrollRef.current = true;
        }
      }
    };

    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
    };
  }, [isBotResponding]);

  useEffect(() => {
    if (isBotResponding) return;
    if (!autoScrollRef.current) return;
    scrollMessagesToBottom();
  }, [messages, isBotResponding, scrollMessagesToBottom]);

  useEffect(() => {
    if (!isBotResponding) return;
    followStreamScroll();
  }, [messages, isBotResponding, followStreamScroll]);

  useEffect(() => {
    if (botStatus === 'thinking') scrollToBottomForNewTurn();
  }, [botStatus, scrollToBottomForNewTurn]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.4) + 'px';
  });

  const stopTyping = () => {
    if (streamingBotMessageId) clearTypewriterPosition(streamingBotMessageId);
    stopStreaming(activeChatIdRef.current, true);
  };

  const runStreamBotResponse = useCallback(
    (params: { chatId: string; userMessage: string; userMessageId: string }) => {
      return streamBotResponse({
        ...params,
        user,
        authorization,
      });
    },
    [streamBotResponse, user, authorization],
  );

  const sendUserMessage = async (messageToSend: string) => {
    const trimmedMessage = messageToSend.trim();
    if (trimmedMessage === '') return;

    const currentChatId = activeChatIdRef.current;

    if (isBotResponding) {
      stopStreaming(currentChatId, true);
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    clearComposerAlert();

    const currentTime = new Date();
    const userMessage: ChatMessageType = {
      id: `user-${currentTime.getTime()}`,
      sender: 'user',
      message: trimmedMessage,
      timestamp: currentTime,
    };

    addUserMessage(currentChatId, userMessage);
    scrollToBottomForNewTurn();

    await runStreamBotResponse({
      chatId: currentChatId,
      userMessage: userMessage.message,
      userMessageId: userMessage.id,
    });
  };

  const handleSendMessage = async () => {
    const messageToSend = inputMessage.trim();
    if (messageToSend === '') return;

    setInputMessage('');
    await sendUserMessage(messageToSend);
  };

  const handleSuggestedQuestionClick = async (suggestedQuestion: string) => {
    if (isBotResponding) return;

    trackEvent({
      name: 'aida_suggested_question',
      buttonId: `aida_suggested_question_${trackingGroup}`,
      label: 'Suggested question',
      page: 'aida',
      section: 'chat_message',
      feature: 'aida',
      eventCategory: 'aida',
    });

    setInputMessage('');
    await sendUserMessage(suggestedQuestion);
  };

  const handleApiEndpointClick = (link: ApiEndpointSearchLink) => {
    trackEvent({
      name: 'aida_api_endpoint_search',
      buttonId: `aida_api_endpoint_search_${trackingGroup}`,
      label: link.label,
      page: 'aida',
      section: 'chat_message',
      feature: 'aida',
      eventCategory: 'aida',
      additionalParams: { search: link.search },
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // allow newline
      } else {
        e.preventDefault();
        trackEvent({
          name: 'aida_send_message',
          buttonId: `aida_send_message_${trackingGroup}`,
          label: 'Send message',
          page: 'aida',
          section: 'chat_input',
          feature: 'aida',
          eventCategory: 'aida',
          additionalParams: { input_method: 'enter_key' },
        });
        handleSendMessage();
      }
    }
  };

  const handleButtonClick = () => {
    trackEvent({
      name: isBotTyping || isBotResponding ? 'aida_stop_response' : 'aida_send_message',
      buttonId: `aida_send_message_${trackingGroup}`,
      label: isBotTyping || isBotResponding ? 'Stop response' : 'Send message',
      page: 'aida',
      section: 'chat_input',
      feature: 'aida',
      eventCategory: 'aida',
      additionalParams: { input_method: isBotTyping || isBotResponding ? 'stop_button' : 'send_button' },
    });
    if (isBotTyping || isBotResponding) {
      stopTyping();
    } else {
      handleSendMessage();
    }
  };

  const handleToggleAidaNotifications = async () => {
    trackEvent({
      name: 'aida_toggle_notifications',
      buttonId: `aida_toggle_notifications_${trackingGroup}`,
      label: aidaNotificationsEnabled ? 'Disable notifications' : 'Enable notifications',
      page: 'aida',
      section: 'sidebar',
      feature: 'aida',
      eventCategory: 'aida',
    });
    if (aidaNotificationsEnabled) {
      setAidaNotificationsEnabled(false);
      return;
    }

    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      window.alert('Your browser does not support system notifications for AIDA.');
      return;
    }

    if (Notification.permission === 'granted') {
      setAidaNotificationsEnabled(true);
      return;
    }

    const permission = await Notification.requestPermission();
    setAidaNotificationsEnabled(permission === 'granted');
    if (permission !== 'granted') {
      window.alert('AIDA notifications were blocked. Enable browser notifications and try again.');
    }
  };

  const formatTimestamp = (timestamp: Date | string) => {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(date);
  };

  const normalizeBackendMarkdown = normalizeBotMarkdown;

  const markdownComponents = {
    a: ({ href, children, ...props }: { href?: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
      if (href?.startsWith('#aida-suggested:')) {
        return <>{children}</>;
      }

      return (
        <a href={href ?? undefined} target="_blank" rel="noopener noreferrer" {...props}>
          {children} <i className="bi bi-box-arrow-up-right"></i>&nbsp;
        </a>
      );
    },
    pre: CodeBlock,
  };

  const composerMarkdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="aida-composer-alert__para">{children}</p>
    ),
    a: ({ href, children }: { href?: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
      if (href?.startsWith('/')) {
        return (
          <Link to={href} className="aida-composer-alert__link">
            {children}
          </Link>
        );
      }
      return (
        <a
          href={href ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="aida-composer-alert__link"
        >
          {children} <i className="bi bi-box-arrow-up-right" aria-hidden />
        </a>
      );
    },
  };

  const shouldRenderBotMessage = (chatMessage: ChatMessageType) => {
    const hasReasoning = Boolean(chatMessage.reasoningSteps?.length);
    const hasAnswer = Boolean(chatMessage.message?.trim() || chatMessage.streamTarget?.trim());
    const isInitialMessage = chatMessage.id === initialBotMessage.id;
    return hasReasoning || hasAnswer || chatMessage.interrupted || isInitialMessage;
  };

  const streamingBotMessageId = [...messages].reverse().find(m => m.sender === 'bot')?.id;

  useEffect(() => {
    revealFinalizeRef.current = false;
  }, [streamingBotMessageId]);

  const handleRevealCaughtUp = useCallback(() => {
    if (revealFinalizeRef.current) return;
    revealFinalizeRef.current = true;
    if (streamingBotMessageId) clearTypewriterPosition(streamingBotMessageId);
    finalizeStreamMessage(activeChatId);
  }, [activeChatId, streamingBotMessageId, finalizeStreamMessage]);

  const handleSyncInterruptedDisplay = useCallback(
    (messageId: string, displayText: string) => {
      syncBotInterruptedDisplay(activeChatId, messageId, displayText);
    },
    [activeChatId, syncBotInterruptedDisplay],
  );

  const handleStreamVisibleTick = useCallback(() => {
    followStreamScroll();
    if (!typingStartedRef.current) {
      typingStartedRef.current = true;
      setIsAnswerTypingVisible(true);
    }
  }, [followStreamScroll]);

  const getActiveChatTitle = () => {
    const firstUserMsg = messages.find(m => m.sender === 'user');
    return session?.customTitle ?? firstUserMsg?.message?.slice(0, 60) ?? 'aida-chat';
  };

  const downloadChatAsTxt = () => {
    trackEvent({
      name: 'aida_export_chat',
      buttonId: `aida_export_chat_${trackingGroup}`,
      label: 'Export chat',
      page: 'aida',
      section: 'sidebar',
      feature: 'aida',
      eventCategory: 'aida',
    });
    const textContent = messages
      ?.map(
        msg =>
          `[${formatTimestamp(msg.timestamp)}] ${msg.sender === 'user' ? 'You' : 'AIDA'}: ${msg.message}`,
      )
      ?.join('\n');
    const filename = `${sanitizeDownloadFilename(getActiveChatTitle())}.txt`;
    triggerBlobDownload(textContent ?? '', filename, 'text/plain;charset=utf-8');
    setDownloadId('downloaded');
    setTimeout(() => setDownloadId(null), 2000);
  };

  const handleRetryBotMessage = async (botMessageId: string) => {
    if (isBotResponding) return;

    const botIndex = messages.findIndex(m => m.id === botMessageId);
    if (botIndex < 0) return;

    let precedingUser: ChatMessageType | undefined;
    for (let i = botIndex - 1; i >= 0; i -= 1) {
      if (messages[i].sender === 'user') {
        precedingUser = messages[i];
        break;
      }
    }
    if (!precedingUser) return;

    trackEvent({
      name: 'aida_retry_response',
      buttonId: `aida_retry_response_${trackingGroup}`,
      label: 'Retry response',
      page: 'aida',
      section: 'chat_message',
      feature: 'aida',
      eventCategory: 'aida',
    });

    const currentChatId = activeChatIdRef.current;
    removeMessagesFrom(currentChatId, botMessageId);
    clearComposerAlert();
    scrollToBottomForNewTurn();

    await runStreamBotResponse({
      chatId: currentChatId,
      userMessage: precedingUser.message,
      userMessageId: precedingUser.id,
    });
  };

  const isAcademicUser = groups?.includes('ROLE_ACADEMIC');
  const canSend = inputMessage.trim().length > 0;

  useEffect(() => {
    if (composerAlert?.chatId === activeChatId) return;
    const propQuestion = shortQuestionProp || questionProp;
    if (activeChatId === initialActiveChatIdRef.current) {
      if (propQuestion) setInputMessage(propQuestion);
      return;
    }
    setInputMessage(propQuestion);
  }, [questionProp, shortQuestionProp, composerAlert, activeChatId]);

  useEffect(() => {
    if (!composerAlert || composerAlert.chatId !== activeChatId) return;
    setInputMessage(composerAlert.restoreDraft);
    const id = window.setTimeout(() => textareaRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [composerAlert, activeChatId]);

  useEffect(() => {
    if (!autoSend || !question || hasAutoSent || !isAuthenticated || !userApiKey) return;
    const timerId = window.setTimeout(() => {
      setHasAutoSent(true);
      setInputMessage('');
      sendUserMessage(question);
    }, 500);
    return () => clearTimeout(timerId);
  }, [autoSend, question, hasAutoSent, isAuthenticated, userApiKey, chatId]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const onViewportChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const mobile = event.matches;
      setIsMobile(mobile);
      setSidebarCollapsed(mobile);
    };

    onViewportChange(mediaQuery);

    const listener = (event: MediaQueryListEvent) => onViewportChange(event);
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, []);

  const handleSaveRename = (id: string) => {
    renameSession(id, editingTitle);
    setEditingSessionId(null);
  };

  const sessionList = Object.entries(chatSessions)
    .map(([id, session]) => {
      const firstUserMsg = session.messages.find(m => m.sender === 'user');
      return {
        id,
        title: session.customTitle ?? firstUserMsg?.message?.slice(0, 60) ?? 'New conversation',
        timestamp: firstUserMsg?.timestamp ?? new Date(0),
        hasMessages: Boolean(firstUserMsg),
      };
    })
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="aida-shell" style={style}>
      {isMobile && !sidebarCollapsed && (
        <button
          className="aida-mobile-backdrop"
          onClick={() => setSidebarCollapsed(true)}
          aria-label="Close menu"
        />
      )}
      {/* ── Sidebar ── */}
      <aside
        className={`aida-sidebar ${sidebarCollapsed ? 'aida-sidebar--collapsed' : ''} ${
          isMobile && !sidebarCollapsed ? 'aida-sidebar--mobile-open' : ''
        }`}
      >
        <div className="aida-sidebar__top">
          <div className="aida-sidebar__logo-wrap">
            {!sidebarCollapsed ? (
              <img src="/static/images/AIDA-logo.svg" alt="AIDA" className="aida-sidebar__logo" />
            ) : (
              <img src="/static/images/eyesAida.svg" alt="AIDA" className="aida-sidebar__logo" />
            )}
          </div>

          <nav className="aida-sidebar__nav">
            <button
              id={`aida_new_chat_${trackingGroup}`}
              className="aida-sidebar__action"
              onClick={handleNewChat}
              title="New chat"
              disabled={hasNewConversationTab}
            >
              <PlusIcon className="aida-sidebar__icon" />
              {!sidebarCollapsed && <span>New Chat</span>}
            </button>

            <button
              className="aida-sidebar__action aida-sidebar__action--danger"
              onClick={() => handleEndChat()}
              title="Clear chat"
              disabled={!hasConversation}
            >
              <TrashIcon className="aida-sidebar__icon" />
              {!sidebarCollapsed && <span>Clear Chat</span>}
            </button>

            <button
              id={`aida_export_chat_${trackingGroup}`}
              className="aida-sidebar__action"
              onClick={downloadChatAsTxt}
              disabled={!hasConversation}
              title="Download chat"
            >
              {downloadId ? (
                <>
                  <CheckIcon className="aida-sidebar__icon" />
                  {!sidebarCollapsed && <span>Downloaded!</span>}
                </>
              ) : (
                <>
                  <ArrowDownTrayOutline className="aida-sidebar__icon" />
                  {!sidebarCollapsed && <span>Export Chat</span>}
                </>
              )}
            </button>

            <button
              id={`aida_toggle_notifications_${trackingGroup}`}
              className={`aida-sidebar__action ${aidaNotificationsEnabled ? 'aida-sidebar__action--active' : ''}`}
              onClick={handleToggleAidaNotifications}
              title={aidaNotificationsEnabled ? 'Disable notifications' : 'Notify me when ready'}
            >
              {aidaNotificationsEnabled ? (
                <BellIcon className="aida-sidebar__icon" />
              ) : (
                <BellSlashIcon className="aida-sidebar__icon" />
              )}
              {!sidebarCollapsed && (
                <span>{aidaNotificationsEnabled ? 'Notifications on' : 'Notify when ready'}</span>
              )}
            </button>

            {sessionList.length > 0 && (
              <div className="aida-sidebar__history">
                {!sidebarCollapsed && (
                  <>
                    <p className="aida-sidebar__privacy-note">
                      <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4 inline-block mb-1" /> Conversation
                      history is stored temporarily to provide context. It is automatically cleared after 24
                      hours or when you use Clear Chat.
                    </p>
                    <p className="aida-sidebar__history-label">Recent</p>
                  </>
                )}
                <ul className="aida-sidebar__history-list">
                  {sessionList.map(s => (
                    <li
                      key={s.id}
                      className={`aida-sidebar__history-item ${s.id === activeChatId ? 'aida-sidebar__history-item--active' : ''}`}
                    >
                      {editingSessionId === s.id ? (
                        <div className="aida-sidebar__history-edit-wrap">
                          <input
                            className="aida-sidebar__history-title-input"
                            value={editingTitle}
                            onChange={e => setEditingTitle(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveRename(s.id);
                              if (e.key === 'Escape') setEditingSessionId(null);
                            }}
                            onBlur={() => handleSaveRename(s.id)}
                            autoFocus
                          />
                        </div>
                      ) : (
                        <>
                          <button
                            className="aida-sidebar__history-btn"
                            onClick={() => handleLoadSession(s.id)}
                            title={s.title}
                          >
                            <ChatBubbleLeftRightIcon className="aida-sidebar__icon" />
                            {!sidebarCollapsed && (
                              <span className="aida-sidebar__history-title">{s.title}</span>
                            )}
                          </button>
                          {!sidebarCollapsed && (
                            <>
                              <button
                                className="aida-sidebar__history-rename"
                                onClick={e => {
                                  e.stopPropagation();
                                  setEditingSessionId(s.id);
                                  setEditingTitle(s.title);
                                }}
                                title="Rename conversation"
                              >
                                <PencilIcon className="h-3 w-3" />
                              </button>
                              <button
                                className="aida-sidebar__history-delete"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteSession(s.id);
                                }}
                                title="Delete conversation"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </nav>
        </div>
        {/* Collapse toggle */}
        <button
          className="aida-sidebar__collapse-btn ml-2 mb-2"
          onClick={() => setSidebarCollapsed(c => !c)}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <ChevronDoubleRightIcon className="aida-sidebar__icon" />
          ) : (
            <ChevronDoubleLeftIcon className="aida-sidebar__icon" />
          )}
        </button>

        <div className="aida-sidebar__bottom">
          {!sidebarCollapsed && (
            <button className="aida-sidebar__disclaimer-toggle" onClick={() => setDisclaimerOpen(o => !o)}>
              <InformationCircleIcon className="aida-sidebar__icon" />
              <span>About AIDA</span>
            </button>
          )}

          {disclaimerOpen && !sidebarCollapsed && (
            <div className="aida-sidebar__disclaimer">
              <p>
                AIDA uses OpenAI technology and RAG to provide responses based on DISGENET data. Outputs may
                contain errors and should be independently verified.{' '}
                <a href="/AIDA-disclaimer" target="_blank" rel="noopener noreferrer">
                  Learn more ↗
                </a>
              </p>
            </div>
          )}
        </div>
      </aside>

      {/* ── Main area ── */}
      <main className="aida-main">
        {isMobile && (
          <button
            className="aida-mobile-menu-btn"
            onClick={() => setSidebarCollapsed(c => !c)}
            aria-label={sidebarCollapsed ? 'Open menu' : 'Close menu'}
            title={sidebarCollapsed ? 'Open menu' : 'Close menu'}
          >
            {sidebarCollapsed ? (
              <ChevronDoubleRightIcon className="aida-sidebar__icon" />
            ) : (
              <ChevronDoubleLeftIcon className="aida-sidebar__icon" />
            )}
          </button>
        )}
        <RestrictedAccess
          isRestricted={!isAuthenticated || isAcademicUser}
          restrictedMessage={
            isAcademicUser ? (
              <h6>
                As an academic user, you do not have access to the chat. Please explore other options
                available in{' '}
                <a href="/plans" style={{ color: '#D02591' }}>
                  plans
                </a>
                .
              </h6>
            ) : undefined
          }
        >
          {/* ── Landing state (no conversation yet) ── */}
          {!hasConversation ? (
            <div className="aida-landing">
              <div className="aida-landing__hero">
                <img src="/static/images/AIDA-removebg.png" alt="AIDA" className="aida-landing__logo" />
                <p className="aida-landing__subtitle">
                  You can ask me scientific data questions. For website navigation assistance or leave
                  feedback, use our Support Bot on the right bottom of the page.
                </p>
              </div>

              <div className="aida-composer-stack aida-composer-stack--landing">
                {showComposerAlert && (
                  <div className="aida-composer-alert" role="alert">
                    <img
                      src="/static/images/eyesAida.svg"
                      alt=""
                      width={24}
                      height={24}
                      className="aida-composer-alert__avatar"
                    />
                    <div className="aida-composer-alert__text">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={composerMarkdownComponents}>
                        {normalizeBackendMarkdown(showComposerAlert.message)}
                      </ReactMarkdown>
                    </div>
                    <button
                      type="button"
                      className="aida-composer-alert__close"
                      onClick={() => clearComposerAlert()}
                      aria-label="Dismiss message"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div className="aida-input-wrap aida-input-wrap--landing">
                  <textarea
                    ref={textareaRef}
                    className="aida-textarea"
                    placeholder={inputPlaceholder}
                    value={inputMessage}
                    onChange={e => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    rows={1}
                    disabled={!isAuthenticated}
                  />
                  <button
                    id={`aida_send_message_${trackingGroup}`}
                    className={`aida-send-btn ${canSend || isBotResponding ? 'aida-send-btn--active' : ''}`}
                    onClick={handleButtonClick}
                    aria-label={isBotResponding ? 'Stop' : 'Send'}
                  >
                    {isBotTyping || isBotResponding ? (
                      <StopIcon className="h-5 w-5" />
                    ) : (
                      <ArrowUpIcon className="h-5 w-5" strokeWidth={2.5} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* ── Active conversation state ── */
            <div className="aida-chat">
              <div className="aida-chat__viewport">
                <div className="aida-chat__messages" ref={messagesAreaRef}>
                  <div className="aida-chat__inner">
                    {messages.map((msg, index) => (
                      <React.Fragment key={`message-${index}`}>
                        {/* User message */}
                        {msg.sender === 'user' && (
                          <div className="aida-msg aida-msg--user" id={`aida-user-msg-${msg.id}`}>
                            <div className="aida-msg__meta">
                              <span className="aida-msg__name">{userName}</span>
                              <span className="aida-msg__time">{formatTimestamp(msg.timestamp)}</span>
                            </div>
                            <div className="aida-msg__bubble">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeHighlight]}
                                components={markdownComponents}
                              >
                                {normalizeBackendMarkdown(msg.message)}
                              </ReactMarkdown>
                            </div>
                          </div>
                        )}

                        {/* Bot message */}
                        {msg.sender === 'bot' && shouldRenderBotMessage(msg) && (
                          <div className="aida-msg aida-msg--bot">
                            <div className="aida-msg__avatar">
                              <img src="/static/images/eyesAida.svg" alt="AIDA" width="28" height="28" />
                            </div>
                            <div className="aida-msg__body">
                              <div className="aida-msg__meta">
                                <span className="aida-msg__name aida-msg__name--bot">AIDA</span>
                                <span className="aida-msg__time">{formatTimestamp(msg.timestamp)}</span>
                              </div>
                              <ReasoningBlock steps={msg.reasoningSteps ?? []} />
                              <BotMessageText
                                msg={msg}
                                isLiveStream={
                                  isBotResponding && msg.sender === 'bot' && index === messages.length - 1
                                }
                                streamBufferClosed={streamBufferClosed}
                                suggestionsDisabled={isBotResponding}
                                searchSource={searchSource}
                                normalizeMarkdown={normalizeBackendMarkdown}
                                markdownComponents={markdownComponents}
                                onRevealCaughtUp={handleRevealCaughtUp}
                                onSyncInterruptedDisplay={handleSyncInterruptedDisplay}
                                onSuggestedQuestionClick={handleSuggestedQuestionClick}
                                onApiEndpointClick={handleApiEndpointClick}
                                onVisibleTick={handleStreamVisibleTick}
                              />
                              {msg.interrupted && (
                                <p className="aida-msg__interrupted-note">Request interrupted by user</p>
                              )}
                              {!(isBotResponding && index === messages.length - 1) &&
                                msg.id !== initialBotMessage.id &&
                                (Boolean(msg.message?.trim()) ||
                                  messages.slice(0, index).some(m => m.sender === 'user')) && (
                                  <BotMessageActions
                                    markdown={msg.message}
                                    canRetry={messages.slice(0, index).some(m => m.sender === 'user')}
                                    disabled={isBotResponding}
                                    onRetry={() => handleRetryBotMessage(msg.id)}
                                    onTrack={action => {
                                      if (action === 'retry') return;
                                      trackEvent({
                                        name: `aida_${action}_response`,
                                        buttonId: `aida_${action}_response_${trackingGroup}`,
                                        label:
                                          action === 'copy' ? 'Copy response' : 'Export response as Markdown',
                                        page: 'aida',
                                        section: 'chat_message',
                                        feature: 'aida',
                                        eventCategory: 'aida',
                                      });
                                    }}
                                  />
                                )}
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}

                    {/* Thinking indicator (only before first streamed content) */}
                    {isBotTyping && !isAnswerTypingVisible && (
                      <div className="aida-msg aida-msg--bot">
                        <div className="aida-msg__avatar">
                          <img src="/static/images/eyesAida.svg" alt="AIDA" width="28" height="28" />
                        </div>
                        <div className="aida-msg__body">
                          <div className="aida-thinking-loader" aria-label="thinking" role="status">
                            <span className="aida-dot"></span>
                            <span className="aida-dot"></span>
                            <span className="aida-dot"></span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {showQuestionRail && (
                  <QuestionRail questions={userQuestions} onQuestionClick={scrollToQuestion} />
                )}
              </div>

              {/* Input bar */}
              <div
                className={`aida-chat__input-area ${isMobile ? 'aida-chat__input-area--mobile' : 'mb-16'}`}
              >
                <div className="aida-composer-stack">
                  {showComposerAlert && (
                    <div className="aida-composer-alert" role="alert">
                      <img
                        src="/static/images/eyesAida.svg"
                        alt=""
                        width={24}
                        height={24}
                        className="aida-composer-alert__avatar"
                      />
                      <div className="aida-composer-alert__text">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={composerMarkdownComponents}>
                          {normalizeBackendMarkdown(showComposerAlert.message)}
                        </ReactMarkdown>
                      </div>
                      <button
                        type="button"
                        className="aida-composer-alert__close"
                        onClick={() => clearComposerAlert()}
                        aria-label="Dismiss message"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <div className="aida-input-wrap">
                    <textarea
                      ref={textareaRef}
                      className="aida-textarea"
                      placeholder={inputPlaceholder}
                      value={inputMessage}
                      onChange={e => setInputMessage(e.target.value)}
                      onKeyDown={handleKeyPress}
                      rows={1}
                    />
                    <button
                      id={`aida_send_message_${trackingGroup}`}
                      className={`aida-send-btn ${canSend || isBotResponding ? 'aida-send-btn--active' : ''}`}
                      onClick={handleButtonClick}
                      aria-label={isBotResponding ? 'Stop' : 'Send'}
                    >
                      {isBotTyping || isBotResponding ? (
                        <StopIcon className="h-5 w-5" />
                      ) : (
                        <ArrowUpIcon className="h-5 w-5" strokeWidth={2.5} />
                      )}
                    </button>
                  </div>
                </div>
                <p className="aida-chat__disclaimer">
                  AIDA can make mistakes. Verify important information.{' '}
                  <a href="/AIDA-disclaimer" target="_blank" rel="noopener noreferrer">
                    Learn more ↗
                  </a>
                </p>
              </div>
            </div>
          )}
        </RestrictedAccess>
      </main>
    </div>
  );
}
